import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { useLocalizedPath } from "@/hooks/useLocalizedPath";
import { useLanguage } from "@/contexts/LanguageContext";
import { ArrowRight, Search, X, Coins, TrendingUp, Trophy, Shield, Scale, Plane } from "lucide-react";
import { Input } from "@/components/ui/input";
import { P } from "@/components/faq/FAQRichContent";
import { faqTranslations } from "@/locales/faq-translations";
import { richAnswersFR } from "@/components/faq/richAnswersFR";
import { richAnswersEN } from "@/components/faq/richAnswersEN";

const catRanges: Record<string, [number, number]> = {
  tarifs: [0, 4], rentabilite: [5, 9], services: [10, 14],
  protection: [15, 19], juridique: [20, 24], voyageurs: [25, 29],
};

const catKeys = ["tarifs", "rentabilite", "services", "protection", "juridique", "voyageurs"];

const catIcons: Record<string, React.ReactNode> = {
  tarifs: <Coins className="w-3.5 h-3.5" />,
  rentabilite: <TrendingUp className="w-3.5 h-3.5" />,
  services: <Trophy className="w-3.5 h-3.5" />,
  protection: <Shield className="w-3.5 h-3.5" />,
  juridique: <Scale className="w-3.5 h-3.5" />,
  voyageurs: <Plane className="w-3.5 h-3.5" />,
};

const cleanLabel = (label: string) => label.replace(/^[^\p{L}]+/u, "");

export default function FAQSection({ showCta = true }: { showCta?: boolean }) {
  const { localePath } = useLocalizedPath();
  const { language } = useLanguage();
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState("tarifs");

  const tr = faqTranslations[language] || faqTranslations.fr;
  const allQuestions = tr.questions;
  const localCategories = tr.categories;

  const questionCategoryMap = useMemo(() => {
    const result: { cat: string; idx: number; q: string }[] = [];
    Object.entries(catRanges).forEach(([cat, [start, end]]) => {
      for (let i = start; i <= end && i < allQuestions.length; i++) {
        result.push({ cat, idx: i, q: allQuestions[i].q });
      }
    });
    return result;
  }, [allQuestions]);

  const faqJsonLd = useMemo(() => ({
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: allQuestions.map(q => ({
      "@type": "Question",
      name: q.q,
      acceptedAnswer: { "@type": "Answer", text: q.a }
    }))
  }), [allQuestions]);

  useEffect(() => {
    const hash = window.location.hash.replace("#", "");
    const validIds = localCategories.map(c => c.id);
    if (hash === "faq") {
      setTimeout(() => {
        document.getElementById("faq")?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 200);
    } else if (hash && validIds.includes(hash)) {
      setActiveTab(hash);
      setTimeout(() => {
        document.getElementById("faq")?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 200);
    }
  }, [localCategories]);

  const handleTabChange = (value: string) => {
    setActiveTab(value);
    history.replaceState(null, "", `#${value}`);
  };

  const isSearching = searchQuery.trim().length > 0;
  const filteredQuestions = useMemo(() => {
    if (!isSearching) return [];
    const term = searchQuery.toLowerCase();
    return questionCategoryMap.filter(item => item.q.toLowerCase().includes(term));
  }, [searchQuery, isSearching, questionCategoryMap]);

  const highlightTerm = (text: string) => {
    if (!isSearching) return text;
    const term = searchQuery.trim();
    if (!term) return text;
    const regex = new RegExp(`(${term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = text.split(regex);
    return parts.map((part, i) =>
      regex.test(part) ? <mark key={i} className="bg-gold/30 text-foreground rounded px-0.5">{part}</mark> : part
    );
  };

  const renderAnswer = (idx: number) => {
    if (language === "fr" && richAnswersFR[idx]) {
      return richAnswersFR[idx];
    }
    if (language === "en" && richAnswersEN[idx]) {
      return richAnswersEN[idx];
    }
    // Plain-text answer with proper formatting for other languages
    const text = allQuestions[idx]?.a;
    if (!text) return null;
    return <P>{text}</P>;
  };

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }} />

      <section id="faq" className="py-12 md:py-16" aria-labelledby="faq-heading">
        <div className="container max-w-4xl mx-auto">
          {/* Header */}
          <header className="text-center mb-8">
            <div className="flex items-center justify-center gap-1.5 sm:gap-4 mb-4">
              <div className="flex flex-col items-end gap-[3px]" aria-hidden="true">
                <span className="w-12 h-px bg-gold/60" />
                <span className="w-8 h-px bg-gold/60" />
                <span className="w-5 h-px bg-gold/60" />
              </div>
              <p className="text-label text-gold">{tr.sectionSubtitle}</p>
              <div className="flex flex-col items-start gap-[3px]" aria-hidden="true">
                <span className="w-12 h-px bg-gold/60" />
                <span className="w-8 h-px bg-gold/60" />
                <span className="w-5 h-px bg-gold/60" />
              </div>
            </div>
            <h2 id="faq-heading" className="text-display text-4xl md:text-5xl text-foreground">
              {tr.sectionTitle}
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto mt-4">
              {tr.sectionDescription}
            </p>
          </header>

          {/* Search bar */}
          <div className="mb-8">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder={tr.searchPlaceholder}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 pr-10 h-12 bg-card border-border text-base"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  aria-label={tr.clearSearch}
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>

          {/* FAQ Content */}
          {isSearching ? (
            <div>
              <p className="text-sm text-muted-foreground mb-4">
                {filteredQuestions.length} {tr.resultsFor} « {searchQuery} »
              </p>
              {filteredQuestions.length === 0 ? (
                <p className="text-muted-foreground text-center py-12">{tr.noResults}</p>
              ) : (
                <Accordion type="single" collapsible className="space-y-2">
                  {filteredQuestions.map((item) => (
                    <AccordionItem key={`search-${item.idx}`} value={`search-${item.idx}`} className="border border-border rounded bg-card px-4">
                      <AccordionTrigger className="text-left text-base md:text-lg font-medium">
                        <span>{highlightTerm(item.q)}</span>
                      </AccordionTrigger>
                      <AccordionContent>
                        {renderAnswer(item.idx)}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              )}
            </div>
          ) : (
          <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
            <TabsList className="w-full h-auto flex-wrap gap-1 bg-transparent p-0 mb-8 justify-center md:justify-center md:flex-nowrap">
              {localCategories.map(cat => (
                <TabsTrigger
                  key={cat.id}
                  value={cat.id}
                  className="inline-flex items-center gap-1.5 text-xs md:text-sm px-3 py-2 whitespace-nowrap data-[state=active]:bg-gold/20 data-[state=active]:text-gold rounded-sm border border-border data-[state=active]:border-gold/40 bg-card"
                >
                  {catIcons[cat.id]}
                  {cleanLabel(cat.label)}
                </TabsTrigger>
              ))}
            </TabsList>

            {catKeys.map(catKey => {
              const [start, end] = catRanges[catKey];
              return (
                <TabsContent key={catKey} value={catKey}>
                  <Accordion type="single" collapsible className="space-y-2">
                    {Array.from({ length: end - start + 1 }, (_, i) => start + i).map(idx => (
                      <AccordionItem key={`q-${idx}`} value={`q-${idx}`} className="border border-border rounded bg-card px-4">
                        <AccordionTrigger className="text-left text-base md:text-lg font-medium">
                          {allQuestions[idx]?.q}
                        </AccordionTrigger>
                        <AccordionContent>
                          {renderAnswer(idx)}
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </TabsContent>
              );
            })}
          </Tabs>
          )}

          {/* CTA */}
          {showCta && (
          <div className="mt-12 text-center border-t border-border pt-12">
            <p className="text-muted-foreground mb-2">{tr.anotherQuestion}</p>
            <Link
              to={localePath("/contact")}
              className="inline-flex items-center gap-2 btn-primary"
            >
              {tr.contactUs} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          )}
        </div>
      </section>
    </>
  );
}
